// Pure helpers for the Availability Overlap report. Given a staff member's
// weekly availability, a client's weekly availability, and the staff's
// recurring commitments, produces a per-day breakdown of:
//   - staff-available blocks
//   - client-available blocks
//   - overlap (intersection) blocks
//   - commitment blocks (the subset of overlap the staff is already booked)
//
// All block boundaries are expressed as minutes-from-midnight so the grid
// renderer can position them without re-parsing `HH:MM` strings.

import {
    DAYS_OF_WEEK,
    minutesToTime,
    timeToMinutes,
    type Availability,
    type DayOfWeek,
    type TimeBlock,
} from './availability';

export interface Interval {
    startMin: number;
    endMin: number;
}

export interface Commitment {
    seriesId: string;
    clientId: string;
    clientName: string;
    /** 0=Sun…6=Sat — native JS getDay() order. */
    dayOfWeek: number;
    startMin: number;
    endMin: number;
}

export interface CommitmentInterval extends Interval {
    clientName: string;
    seriesId: string;
}

export interface OverlapDay {
    day: DayOfWeek;
    staff: Interval[];
    client: Interval[];
    overlap: Interval[];
    commitments: CommitmentInterval[];
}

// JS getDay() order to our day keys. Mirrors `dayKeyFromDate` in availability.ts
// but indexed instead of date-typed because commitments arrive as plain ints.
const JS_DAY_TO_KEY: DayOfWeek[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

function blocksToIntervals(blocks: TimeBlock[] | undefined): Interval[] {
    if (!blocks) return [];
    return blocks
        .map((b) => ({ startMin: timeToMinutes(b.start), endMin: timeToMinutes(b.end) }))
        .filter((i) => Number.isFinite(i.startMin) && Number.isFinite(i.endMin) && i.endMin > i.startMin)
        .sort((a, b) => a.startMin - b.startMin);
}

/**
 * Intersection of two sets of intervals. Inputs are expected sorted + normalized
 * (end > start). Output is the set of sub-intervals present in both.
 */
export function intersectIntervals(a: Interval[], b: Interval[]): Interval[] {
    const out: Interval[] = [];
    let i = 0;
    let j = 0;
    while (i < a.length && j < b.length) {
        const startMin = Math.max(a[i].startMin, b[j].startMin);
        const endMin = Math.min(a[i].endMin, b[j].endMin);
        if (endMin > startMin) out.push({ startMin, endMin });
        if (a[i].endMin < b[j].endMin) i++;
        else j++;
    }
    return out;
}

/**
 * Clips commitments to a set of overlap intervals. A commitment only matters
 * if it falls inside a time window where both staff and client are available —
 * a commitment that sits outside the overlap is irrelevant to staffing the
 * target client, so the report doesn't draw it.
 */
export function clipCommitmentsToOverlap(
    commitments: Commitment[],
    overlap: Interval[],
): CommitmentInterval[] {
    const out: CommitmentInterval[] = [];
    for (const c of commitments) {
        for (const o of overlap) {
            const startMin = Math.max(c.startMin, o.startMin);
            const endMin = Math.min(c.endMin, o.endMin);
            if (endMin > startMin) {
                out.push({
                    startMin,
                    endMin,
                    clientName: c.clientName,
                    seriesId: c.seriesId,
                });
            }
        }
    }
    return out.sort((a, b) => a.startMin - b.startMin);
}

export function computeOverlap(
    staffAvailability: Availability | null | undefined,
    clientAvailability: Availability | null | undefined,
    commitments: Commitment[],
): OverlapDay[] {
    const staffAvail = staffAvailability ?? {};
    const clientAvail = clientAvailability ?? {};
    const byDay = new Map<DayOfWeek, Commitment[]>();
    for (const c of commitments) {
        const key = JS_DAY_TO_KEY[c.dayOfWeek];
        if (!key) continue;
        const list = byDay.get(key) ?? [];
        list.push(c);
        byDay.set(key, list);
    }
    return DAYS_OF_WEEK.map((day) => {
        const staff = blocksToIntervals(staffAvail[day]);
        const client = blocksToIntervals(clientAvail[day]);
        const overlap = intersectIntervals(staff, client);
        // Commitments stay raw — the staff bar shows them in full, regardless
        // of whether the selected client has availability at the same time.
        // The KPI helpers clip to overlap on the fly when that's what matters.
        const dayCommitments: CommitmentInterval[] = (byDay.get(day) ?? [])
            .map((c) => ({
                startMin:   c.startMin,
                endMin:     c.endMin,
                clientName: c.clientName,
                seriesId:   c.seriesId,
            }))
            .sort((a, b) => a.startMin - b.startMin);
        return { day, staff, client, overlap, commitments: dayCommitments };
    });
}

/**
 * Sum the total overlap minutes across the week. Used for the KPI strip.
 * Does not subtract commitment minutes — "raw" overlap is the headline; the
 * commitment-net total is a separate metric the caller computes too.
 */
export function totalOverlapMinutes(days: OverlapDay[]): number {
    let total = 0;
    for (const d of days) {
        for (const iv of d.overlap) total += iv.endMin - iv.startMin;
    }
    return total;
}

/**
 * Minutes of commitment that fall inside the staff×client overlap. This is the
 * "Staff commitments in overlap" KPI — the slice of overlap that's already
 * unavailable. Computed by intersecting each day's commitments against the
 * day's overlap, since commitments are stored raw (unclipped) now.
 */
export function totalCommitmentMinutes(days: OverlapDay[]): number {
    let total = 0;
    for (const d of days) {
        for (const c of d.commitments) {
            for (const o of d.overlap) {
                const s = Math.max(c.startMin, o.startMin);
                const e = Math.min(c.endMin, o.endMin);
                if (e > s) total += e - s;
            }
        }
    }
    return total;
}

/**
 * Inverted: overlap with commitments subtracted. A contiguous overlap with
 * one commitment in the middle splits into two free intervals. Used both for
 * the KPI total and — in the future — for suggesting exact free slots.
 */
export function subtractCommitments(
    overlap: Interval[],
    commitments: CommitmentInterval[],
): Interval[] {
    if (commitments.length === 0) return overlap;
    const merged = mergeIntervals(commitments.map((c) => ({ startMin: c.startMin, endMin: c.endMin })));
    const out: Interval[] = [];
    for (const o of overlap) {
        let cursor = o.startMin;
        for (const c of merged) {
            if (c.endMin <= cursor) continue;
            if (c.startMin >= o.endMin) break;
            if (c.startMin > cursor) out.push({ startMin: cursor, endMin: Math.min(c.startMin, o.endMin) });
            cursor = Math.max(cursor, c.endMin);
            if (cursor >= o.endMin) break;
        }
        if (cursor < o.endMin) out.push({ startMin: cursor, endMin: o.endMin });
    }
    return out;
}

/** `a - b`: intervals in `a` with every minute that falls in `b` removed. */
export function subtractIntervals(a: Interval[], b: Interval[]): Interval[] {
    if (b.length === 0) return a.map((i) => ({ ...i }));
    const merged = mergeIntervals(b);
    const out: Interval[] = [];
    for (const o of a) {
        let cursor = o.startMin;
        for (const c of merged) {
            if (c.endMin <= cursor) continue;
            if (c.startMin >= o.endMin) break;
            if (c.startMin > cursor) out.push({ startMin: cursor, endMin: Math.min(c.startMin, o.endMin) });
            cursor = Math.max(cursor, c.endMin);
            if (cursor >= o.endMin) break;
        }
        if (cursor < o.endMin) out.push({ startMin: cursor, endMin: o.endMin });
    }
    return out;
}

function mergeIntervals(ivs: Interval[]): Interval[] {
    if (ivs.length === 0) return [];
    const sorted = [...ivs].sort((a, b) => a.startMin - b.startMin);
    const out: Interval[] = [sorted[0]];
    for (let i = 1; i < sorted.length; i++) {
        const last = out[out.length - 1];
        const cur = sorted[i];
        if (cur.startMin <= last.endMin) last.endMin = Math.max(last.endMin, cur.endMin);
        else out.push({ ...cur });
    }
    return out;
}

/** Format a minute-of-day as `9:30 AM` / `1:45 PM`. */
export function formatMin(min: number): string {
    return (
        // Reuse the availability formatter for parity with the rest of the app.
        // timeToMinutes + minutesToTime round-trip preserves the value.
        formatHhmm12(minutesToTime(min))
    );
}

function formatHhmm12(hhmm: string): string {
    const mins = timeToMinutes(hhmm);
    if (!Number.isFinite(mins)) return hhmm;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const suffix = h >= 12 ? 'PM' : 'AM';
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
}
